import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useAuth } from "@/context/AuthContext";

const Shell = ({ eyebrow, title, children, testid }) => (
  <div className="min-h-[70vh] grid place-items-center px-4 py-10">
    <div className="card-warm p-6 sm:p-8 w-full max-w-md" data-testid={testid}>
      <div className="eyebrow mb-2">{eyebrow}</div>
      <h1 className="font-display text-3xl mb-6">{title}</h1>
      {children}
    </div>
  </div>
);

const Field = ({ label, testid, ...props }) => (
  <label className="block mb-4">
    <span className="block text-sm font-semibold mb-1">{label}</span>
    <input {...props} className="w-full bg-white border border-[var(--border-warm)] rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-[var(--ink)]" data-testid={testid}/>
  </label>
);

const POLICY = "At least 8 characters, including a letter and a number.";

const usePasswordForm = () => {
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const mismatch = pw.confirm && pw.next !== pw.confirm;
  return { pw, setPw, mismatch };
};

const NewPasswordFields = ({ pw, setPw, mismatch }) => (
  <>
    <Field label="New password" type="password" autoComplete="new-password" required minLength={8} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} testid="new-password-input"/>
    <Field label="Confirm new password" type="password" autoComplete="new-password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} testid="confirm-password-input"/>
    <p className={`text-xs mb-4 ${mismatch ? "text-red-600" : "text-zinc-500"}`} data-testid="password-hint">{mismatch ? "Passwords do not match." : POLICY}</p>
  </>
);

const Msg = ({ err, ok }) => (
  <>
    {err && <div className="text-sm text-red-600 mb-3" data-testid="password-error">{err}</div>}
    {ok && <div className="text-sm text-emerald-700 mb-3" data-testid="password-success">{ok}</div>}
  </>
);

const SubmitBtn = ({ busy, label, disabled }) => (
  <button disabled={busy || disabled} className="btn-terracotta w-full py-3 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="password-submit-btn">
    {busy ? "Please wait…" : label}
  </button>
);

export function AccountPage() {
  useSeo({ title: "My account", path: "/account", noindex: true });
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const form = usePasswordForm();
  const [state, setState] = useState({ busy: false, err: "", ok: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, err: "", ok: "" });
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: form.pw.next });
      setCurrent(""); form.setPw({ next: "", confirm: "" });
      if (user?.must_change_password) setUser({ ...user, must_change_password: false });
      setState({ busy: false, err: "", ok: "Password changed. Other devices have been signed out and a confirmation email was sent." });
    } catch (ex) {
      setState({ busy: false, err: formatApiError(ex.response?.data?.detail) || ex.message, ok: "" });
    }
  };

  return (
    <Shell eyebrow="My account" title="Change password" testid="account-page">
      {user?.must_change_password && (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="must-change-password-note">
          Welcome! For security, please replace the temporary password from your welcome email with your own password before continuing.
        </div>
      )}
      <div className="text-sm text-zinc-600 mb-6" data-testid="account-identity">Signed in as <b>{user?.name}</b> · {user?.email}</div>
      <form onSubmit={submit}>
        <Field label="Current password" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} testid="current-password-input"/>
        <NewPasswordFields {...form}/>
        <Msg err={state.err} ok={state.ok}/>
        <SubmitBtn busy={state.busy} label="Update password" disabled={form.mismatch}/>
      </form>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  useSeo({ title: "Forgot password", path: "/forgot-password", noindex: true });
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ busy: false, err: "", ok: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, err: "", ok: "" });
    try {
      await api.post("/auth/forgot-password", { email });
      setState({ busy: false, err: "", ok: "If an account exists for that email, a reset link is on its way. It expires in 1 hour." });
    } catch (ex) {
      setState({ busy: false, err: formatApiError(ex.response?.data?.detail) || ex.message, ok: "" });
    }
  };

  return (
    <Shell eyebrow="Account help" title="Forgot password" testid="forgot-password-page">
      <form onSubmit={submit}>
        <Field label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} testid="forgot-email-input"/>
        <Msg err={state.err} ok={state.ok}/>
        <SubmitBtn busy={state.busy} label="Email me a reset link"/>
      </form>
      <div className="text-sm text-zinc-600 mt-4 text-center"><Link to="/login" className="font-semibold text-[var(--terracotta)]" data-testid="back-to-login">Back to log in</Link></div>
    </Shell>
  );
}

export function ResetPasswordPage() {
  useSeo({ title: "Choose a new password", path: "/reset-password", noindex: true });
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const form = usePasswordForm();
  const [state, setState] = useState({ busy: false, err: "", ok: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, err: "", ok: "" });
    try {
      await api.post("/auth/reset-password", { token, new_password: form.pw.next });
      setState({ busy: false, err: "", ok: "Your password has been reset. You can now log in." });
    } catch (ex) {
      setState({ busy: false, err: formatApiError(ex.response?.data?.detail) || ex.message, ok: "" });
    }
  };

  if (!token) return <Shell eyebrow="Account help" title="Link missing" testid="reset-password-page"><p className="text-sm">This reset link is incomplete. <Link to="/forgot-password" className="underline">Request a new one</Link>.</p></Shell>;
  return (
    <Shell eyebrow="Account help" title="Choose a new password" testid="reset-password-page">
      {state.ok ? (
        <><Msg ok={state.ok}/><Link to="/login" className="btn-terracotta block text-center w-full py-3 rounded-full text-sm font-semibold" data-testid="reset-go-login">Log in</Link></>
      ) : (
        <form onSubmit={submit}>
          <NewPasswordFields {...form}/>
          <Msg err={state.err}/>
          <SubmitBtn busy={state.busy} label="Reset password" disabled={form.mismatch}/>
        </form>
      )}
    </Shell>
  );
}
